import React from "react";
import { useAlarmCardCount } from "./AlarmOverviewDataGrid/hooks";
import { Grid, CircularProgress } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { useSelector } from "react-redux";
import { selectedGeoRegionsSelector } from "../../store/api/admin/selectors";
import { Region } from "../../store/api/admin/types";
import CardsDisplay from "../../components/Shared/Cards/CardsDisplay";

interface AlarmCardConfig {
  title: string;
  icon: React.ReactNode;
  getValue: (data: any) => number;
  onClick?: () => void;
}

const AlarmOverviewCards: React.FC<{ alarmsGridId?: string; onSeveritySelect?: (severity: string) => void }> = ({ alarmsGridId = "alarm-overview-alarms", onSeveritySelect }) => {
  const selectedGeoRegions = useSelector(selectedGeoRegionsSelector) as Region[];
  const regionNames = selectedGeoRegions.map(r => r.name);
  const { data, isLoading, error } = useAlarmCardCount(regionNames);

  function scrollToAlarmsAndFilter(title: string): void {
    if (onSeveritySelect) {
      onSeveritySelect(title);
      return;
    }

    const target = document.getElementById(alarmsGridId);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    window.dispatchEvent(
      new CustomEvent("alarm-overview:filter", {
        detail: { severity: title },
      })
    );
  }

  const alarmCards: AlarmCardConfig[] = [
    {
      title: "Critical",
      icon: <ErrorIcon sx={{ fontSize: 16, color: "var(--destructive)" }} />,
      getValue: (d) => d?.criticalAlarmCount || 0,
      onClick: () => scrollToAlarmsAndFilter("Critical"),
    },
    {
      title: "Major",
      icon: <ErrorOutlineIcon sx={{ fontSize: 16, color: "var(--warning)" }} />,
      getValue: (d) => d?.majorAlarmCount || 0,
      onClick: () => scrollToAlarmsAndFilter("Major"),
    },
    {
      title: "Minor",
      icon: <WarningIcon sx={{ fontSize: 16, color: "var(--warning)" }} />,
      getValue: (d) => d?.minorAlarmCount || 0,
      onClick: () => scrollToAlarmsAndFilter("Minor"),
    },
  ];

  const renderAlarmCard = (config: AlarmCardConfig) => {
    const value = config.getValue(data);

    return (
      <div>
        <div className="text-2xl font-semibold tracking-tight" style={{ color: "var(--foreground)" }}>
          {value}
        </div>
      </div>
    );
  };

  return (
    <Grid container spacing={3}>
      {alarmCards.map((card) => (
        <Grid item xs={12} sm={6} md={4} key={card.title}>
          <CardsDisplay
            title={card.title}
            titleRight={card.icon}
            sx={{ backgroundColor: "var(--card)", height: "100%" }}
            onClick={card.onClick}
            ariaLabel={`Filter alarms by ${card.title} severity`}
            contents={
              isLoading ? (
                <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
              ) : (
                renderAlarmCard(card)
              )
            }
          />
        </Grid>
      ))}
    </Grid>
  );
};


export default AlarmOverviewCards;
